import { Component, OnDestroy, inject, signal, effect, ElementRef, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DOCUMENT } from '@angular/common';

// Components
import { UiLoading } from '@components/ui/loading/loading';

// Services
import { DictKeyService } from '@core/services/app/dict-key.service';
import { DictKeyEditService } from '@core/services/app/dict-key-edit.service';
import { TopbarService } from '@core/services/layouts/topbar.service';

// Models
import { DictKeyEntry } from '@core/models/app/dict-key.model';

// Utils
import { Dic } from '@core/utils/dic.utils';

// PrimeNG
import { InputTextModule } from 'primeng/inputtext';
import { CheckboxModule } from 'primeng/checkbox';
import { ButtonModule } from 'primeng/button';

/**
 * Popover d'édition inline d'une entrée du dictionnaire, ouvert au clic droit.
 * Placé une seule fois dans `app.html` : il se positionne sur les coordonnées portées
 * par `DictKeyService.popoverState`, pas sur le rect de l'élément cliqué.
 */
@Component({
	selector: 'app-dictkeyedit',
	imports: [FormsModule, UiLoading, InputTextModule, CheckboxModule, ButtonModule],
	templateUrl: './dict-key-edit.html',
	styleUrl: './dict-key-edit.scss',
})
export class AppDictKeyEdit implements OnDestroy {
	protected readonly Dic = Dic;

	@ViewChild('popover') popoverRef!: ElementRef<HTMLDivElement>;

	private document = inject(DOCUMENT);

	constructor(
		public dictKeyService: DictKeyService,
		private dictKeyEditService: DictKeyEditService,
		private topbarService: TopbarService,
	) {
		effect(() => {
			const state = this.dictKeyService.popoverState();
			if (state) {
				this.loadEntry(state.field);
				this.computePosition(state.x, state.y);
			} else {
				this.removeListeners();
			}
		});
	}

	loading = signal(true);
	saving = signal(false);
	posX = signal(0);
	posY = signal(0);
	arrowLeft = signal(0);
	arrowBelow = signal(false);

	readonly POPOVER_WIDTH = 320;
	readonly POPOVER_HEIGHT = 260;

	entry: DictKeyEntry = {
		field: '',
		fr: '',
		en: '',
		shared: false,
	};

	private outsideListener = (event: MouseEvent) => {
		if (this.popoverRef && !this.popoverRef.nativeElement.contains(event.target as Node)) {
			this.close();
		}
	};

	private keyListener = (event: KeyboardEvent) => {
		if (event.key === 'Escape') this.close();
	};

	private computePosition(clickX: number, clickY: number) {
		const margin = 8;
		const arrowSize = 8;
		const vw = window.innerWidth;
		const vh = window.innerHeight;

		let x = clickX - this.POPOVER_WIDTH / 2;
		x = Math.max(margin, Math.min(x, vw - this.POPOVER_WIDTH - margin));

		const arrowL = clickX - x;
		this.arrowLeft.set(Math.max(12, Math.min(arrowL, this.POPOVER_WIDTH - 12)));

		let y = clickY - this.POPOVER_HEIGHT - arrowSize - margin;
		let below = false;
		if (y < margin) {
			y = clickY + arrowSize + margin;
			below = true;
		}
		y = Math.max(margin, Math.min(y, vh - this.POPOVER_HEIGHT - margin));

		this.posX.set(x);
		this.posY.set(y);
		this.arrowBelow.set(below);

		setTimeout(() => {
			this.document.addEventListener('mousedown', this.outsideListener);
			this.document.addEventListener('keydown', this.keyListener);
		}, 0);
	}

	private loadEntry(field: string) {
		this.loading.set(true);
		this.dictKeyEditService.getEntry(field).subscribe({
			next: (response) => {
				const data = ('data' in response ? response.data : response) as Partial<DictKeyEntry> | undefined;
				this.entry = {
					field,
					fr: data?.fr ?? '',
					en: data?.en ?? '',
					shared: !!data?.shared,
				};
				this.loading.set(false);
			},
			error: () => {
				this.entry = {
					field,
					fr: '',
					en: '',
					shared: false,
				};
				this.loading.set(false);
			},
		});
	}

	onSave() {
		this.saving.set(true);
		this.dictKeyEditService.saveEntry(this.entry).subscribe({
			next: () => {
				this.topbarService.reloadDict().subscribe({
					next: () => {
						this.saving.set(false);
						this.close();
					},
					error: () => {
						this.saving.set(false);
						this.close();
					},
				});
			},
			error: () => {
				this.saving.set(false);
			},
		});
	}

	close() {
		this.removeListeners();
		this.dictKeyService.close();
	}

	private removeListeners() {
		this.document.removeEventListener('mousedown', this.outsideListener);
		this.document.removeEventListener('keydown', this.keyListener);
	}

	ngOnDestroy() {
		this.removeListeners();
	}
}
